import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { type Persistor, persistStore } from 'redux-persist'

import { permissionsReducer } from '#/store/ducks/permissions'

import appReducer from './ducks/app'
import authReducer from './ducks/auth'
import companyReducer from './ducks/company'
import contextReducer from './ducks/context'
import farmReducer from './ducks/farm'

const buildStore = () =>
  configureStore({
    middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }),
    reducer: combineReducers({
      app: appReducer,
      auth: authReducer,
      company: companyReducer,
      context: contextReducer,
      farm: farmReducer,
      permissions: permissionsReducer,
    }),
  })

/**
 * Ducks (reducers/context/auth/farm/etc) não exportam componentes React, então uma edição em
 * qualquer um deles não pode ser hot-swapped pelo React Fast Refresh: a invalidação do HMR sobe
 * até este módulo e ele é re-executado. Sem o guard abaixo, `configureStore`/`persistStore`
 * criariam uma nova store a cada save, resetando farm/animal/permissions em memória sem que os
 * componentes já montados (que não remontam) refaçam o fetch — dando a impressão de que o app
 * "perdeu o contexto" atual. Reaproveitar a store existente entre reloads do HMR evita isso.
 */
type AppStore = ReturnType<typeof buildStore>

const store = (import.meta.hot?.data.store as AppStore | undefined) ?? buildStore()

const persistor = (import.meta.hot?.data.persistor as Persistor | undefined) ?? persistStore(store)

if (import.meta.hot) {
  import.meta.hot.data.store = store
  import.meta.hot.data.persistor = persistor
  import.meta.hot.accept()
}

export { store, persistor }
